import React, { useEffect, useState, useRef } from "react";
import { UploadProgressBar } from "../components/UploadProgressBar";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Thread } from "../components/Thread";
import { ConversationListPanel } from "../components/ConversationListPanel";
import { ConversationInfoDialog } from "../components/ConversationInfoDialog";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ArrowLeft, Briefcase, Users, UsersRound, MapPin } from "lucide-react";
import { mediaSrcById } from "../api";
import { useTypingPresence, presenceLabel } from "../lib/typing";
import { offlineEnabled, readConversation } from "../lib/offlineSync";

function initials(name) {
  return (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function ChatThread() {
  const { convId } = useParams();
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conv, setConv] = useState(null);
  const [contextJob, setContextJob] = useState(null);
  const jobParam = searchParams.get("job");
  const msgParam = searchParams.get("msg");

  const fetchConv = () => api.get(`/conversations/${convId}?user_id=${user.id}`).then((res) => setConv(res.data)).catch(async () => {
    // no signal: open the copy kept on this phone (offline mode)
    if (offlineEnabled(user.id)) { const c = await readConversation(convId); if (c) setConv(c); }
  });
  useEffect(() => {
    setConv(null);
    fetchConv();
  }, [convId, user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadConv = () => { fetchConv(); };

  // Job context: opened from inside a Job Hub -> auto Job Topic Mode + filtered view
  useEffect(() => {
    if (!jobParam) {
      setContextJob(null);
      return;
    }
    api.get(`/jobs/${jobParam}`).then((res) => {
      setContextJob({ id: res.data.id, job_number: res.data.job_number, customer_name: res.data.customer_name });
    }).catch(() => setContextJob(null));
  }, [jobParam]);

  const waitingJob = jobParam && !contextJob;

  // ---- Mobile edge-swipe back: swipe from the LEFT EDGE to return to Messages ----
  // The page visually follows the finger (native-app feel) and slides out on release.
  const edgeSwipeRef = useRef(null);
  const [swipeX, setSwipeX] = useState(0);
  const overlayOpen = () =>
    !!document.querySelector('[data-testid="photo-viewer"], [data-testid="photo-markup-editor"], [data-testid="video-viewer"], [role="dialog"][data-state="open"]');
  const onEdgeTouchStart = (e) => {
    if (e.touches.length !== 1) return;
    const t0 = e.touches[0];
    // Only start tracking when the gesture begins at the very left edge of the screen
    if (t0.clientX > 32 || overlayOpen()) { edgeSwipeRef.current = null; return; }
    edgeSwipeRef.current = { x: t0.clientX, y: t0.clientY, t: Date.now() };
  };
  const onEdgeTouchMove = (e) => {
    const s = edgeSwipeRef.current;
    if (!s) return;
    const t0 = e.touches[0];
    const dx = Math.max(0, t0.clientX - s.x);
    const dy = Math.abs(t0.clientY - s.y);
    // Mostly-vertical movement = the user is scrolling, abort the gesture
    if (dy > 70 && dx < 40) {
      edgeSwipeRef.current = null;
      setSwipeX(0);
      return;
    }
    setSwipeX(dx);
  };
  const onEdgeTouchEnd = (e) => {
    const s = edgeSwipeRef.current;
    edgeSwipeRef.current = null;
    if (!s) return;
    const c = e.changedTouches && e.changedTouches[0];
    if (!c) { setSwipeX(0); return; }
    const dx = c.clientX - s.x;
    const dy = Math.abs(c.clientY - s.y);
    const ok = Date.now() - s.t <= 800 && dx >= 70 && dx >= dy * 1.5 && !overlayOpen();
    if (ok) {
      // Finish the slide-out, then navigate back to the contact list
      setSwipeX(typeof window !== "undefined" ? window.innerWidth : 400);
      setTimeout(() => navigate("/messages"), 160);
    } else {
      setSwipeX(0); // spring back
    }
  };

  return (
    // NOTE: the app shell already constrains the viewport height — this pane just
    // fills it. The thread (right) and the people list (left) each get their own
    // independent internal scroll.
    <div
      className="flex w-full flex-1 min-h-0 overflow-hidden"
      onTouchStart={onEdgeTouchStart}
      onTouchMove={onEdgeTouchMove}
      onTouchEnd={onEdgeTouchEnd}
      style={{
        transform: swipeX ? `translateX(${swipeX}px)` : "none",
        transition: edgeSwipeRef.current ? "none" : "transform 180ms ease-out",
        boxShadow: swipeX > 0 ? "-16px 0 32px rgba(0,0,0,0.22)" : "none",
        willChange: swipeX ? "transform" : "auto",
      }}
      data-testid="chat-thread-page"
    >
      {/* Desktop split-view: conversation list on the left for fast switching */}
      <aside className="hidden md:flex md:flex-col w-72 xl:w-80 border-r shrink-0 min-h-0" data-testid="chat-sidebar-container">
        <ConversationListPanel activeConvId={convId} />
      </aside>

      {/* Active thread pane */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {!conv || waitingJob ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">...</div>
        ) : (
          <ThreadPane key={`${conv.id}-${contextJob?.id || ""}-${msgParam || ""}`} conv={conv} contextJob={contextJob} focusMessageId={msgParam} navigate={navigate} t={t} reloadConv={reloadConv} />
        )}
      </div>
    </div>
  );
}

function ThreadPane({ conv, contextJob, focusMessageId, navigate, t, reloadConv }) {
  const [infoOpen, setInfoOpen] = useState(false);
  const { user } = useApp();
  const presence = useTypingPresence(conv.id, user.id);
  const presenceText = presenceLabel(presence, t);
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Thread header (container has fixed height + internal scroll — no sticky needed) */}
      <div className="border-b bg-background px-4 py-2.5 flex items-center gap-3 shrink-0 z-30">
        <Button variant="ghost" size="icon" data-testid="thread-back-button" onClick={() => navigate(-1)} className="h-9 w-9 shrink-0">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        {conv.type === "dm" && conv.other_user && (
          <Avatar className="h-9 w-9 shrink-0 cursor-pointer" onClick={() => setInfoOpen(true)}>
            {conv.other_user.avatar_media_id && (
              <AvatarImage src={mediaSrcById(conv.other_user.avatar_media_id)} alt="" className="object-cover" />
            )}
            <AvatarFallback style={{ backgroundColor: conv.other_user.avatar_color, color: "#fff" }} className="text-xs font-semibold">
              {initials(conv.other_user.name)}
            </AvatarFallback>
          </Avatar>
        )}
        {conv.type === "group" && (
          <div
            onClick={() => setInfoOpen(true)}
            className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 cursor-pointer overflow-hidden ${conv.job_id ? "bg-primary/10 text-primary" : "bg-accent/20 text-accent-foreground"}`}
          >
            {conv.avatar_media_id ? (
              <img src={mediaSrcById(conv.avatar_media_id)} alt="" className="h-full w-full object-cover" />
            ) : (
              <UsersRound className="h-5 w-5" />
            )}
          </div>
        )}
        <div
          className="flex-1 min-w-0 cursor-pointer rounded-lg -mx-1 px-1 py-0.5 hover:bg-muted/50 transition-colors"
          data-testid="thread-header-info"
          onClick={() => setInfoOpen(true)}
        >
          <div className="font-medium text-sm truncate flex items-center gap-2">
            {conv.type === "crew" && <Users className="h-4 w-4 text-accent-foreground shrink-0" />}
            {conv.name}
            {conv.type === "group" && conv.job && (
              <Badge variant="outline" className="text-[9px] px-1.5 font-mono-plex text-primary border-primary/40 shrink-0" data-testid="thread-job-badge">
                #{conv.job.job_number}
              </Badge>
            )}
          </div>
          {presenceText ? (
            <div className="text-xs text-emerald-600 truncate font-medium" data-testid="thread-presence">{presenceText}</div>
          ) : (<>
          {conv.type === "dm" && conv.other_user && (
            <div className="text-xs text-muted-foreground truncate">
              {conv.other_user.title}
              {conv.other_user.crew_id ? ` · ${conv.other_user.crew_id.replace("crew-", "").toUpperCase()}` : (conv.other_user.title !== t(conv.other_user.role) ? ` · ${t(conv.other_user.role)}` : "")}
            </div>
          )}
          {conv.type === "group" && (
            <div className="text-xs text-muted-foreground truncate">
              {conv.participants?.length} {t("members")}
              {conv.job ? ` · ${conv.job.customer_name}` : ""}
            </div>
          )}
          {conv.type === "crew" && (
            <div className="text-xs text-muted-foreground">{conv.participants?.length} {t("crew").toLowerCase()}</div>
          )}
          </>)}
        </div>
        {conv.type === "group" && conv.job && (
          <Button
            variant="outline"
            size="sm"
            data-testid="open-job-hub-button"
            onClick={() => navigate(`/jobs/${conv.job.id}`)}
            className="shrink-0"
          >
            <Briefcase className="h-4 w-4 mr-1.5" />
            <span className="hidden sm:inline">{t("open_job_hub")}</span>
            <span className="sm:hidden">Hub</span>
          </Button>
        )}
      </div>

      <UploadProgressBar conversationId={conv.id} />
      <Thread key={conv.id} conversation={conv} contextJob={contextJob} focusMessageId={focusMessageId} />

      <ConversationInfoDialog open={infoOpen} onClose={() => setInfoOpen(false)} conversation={conv} onChanged={reloadConv} />
    </div>
  );
}
